import { useSearchParams, type SetURLSearchParams } from "react-router";
import { type SortingState, type Updater } from "@tanstack/react-table";
import { type DocumentVersionSummary } from "~/api/types";
import { QueryParams, useDocumentVersions } from "~/api/v2/useDocumentVersions";
import { filterDocumentVersions } from "~/helpers/documentFilters";
import { useDocumentVersionFilters } from "./useDocumentVersionFilters";

const sortParam = "sort";

const defaultSorting: SortingState = [{ id: "title", desc: false }];

const parseSortingFromParams = (
  searchParams: URLSearchParams,
): SortingState => {
  const sortValue = searchParams.get(sortParam);
  if (!sortValue) return defaultSorting;
  const [id, direction] = sortValue.split(":");
  if (!id || (direction !== "asc" && direction !== "desc"))
    return defaultSorting;
  return [{ id, desc: direction === "desc" }];
};

const updateSearchParams = (
  searchParams: URLSearchParams,
  setSearchParams: SetURLSearchParams,
  apply: (next: URLSearchParams) => void,
) => {
  const next = new URLSearchParams(searchParams);
  apply(next);
  setSearchParams(next);
};

export const useDocumentVersionsListModel = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const titleSearch = searchParams.get(QueryParams.Title);
  const { data, isLoading, mutate } = useDocumentVersions({
    includeWorkflowData: true,
    titleSearch,
  });

  const documentVersions = data ?? [];
  const filters = useDocumentVersionFilters({
    documentVersions,
    searchParams,
    setSearchParams,
  });
  const sorting = parseSortingFromParams(searchParams);
  const filteredDocumentVersions: DocumentVersionSummary[] =
    filterDocumentVersions(documentVersions, filters.activeFilters);

  const handleSortingChange = (updater: Updater<SortingState>) => {
    const nextSorting =
      typeof updater === "function" ? updater(sorting) : updater;

    updateSearchParams(searchParams, setSearchParams, (next) => {
      const firstSort = nextSorting[0];
      if (firstSort) {
        next.set(
          sortParam,
          `${firstSort.id}:${firstSort.desc ? "desc" : "asc"}`,
        );
      } else {
        next.delete(sortParam);
      }
    });
  };

  const handleTitleSearchChange = (value: string) => {
    updateSearchParams(searchParams, setSearchParams, (next) => {
      if (value) {
        next.set(QueryParams.Title, value);
      } else {
        next.delete(QueryParams.Title);
      }
    });
  };

  return {
    documentVersions,
    filteredDocumentVersions,
    filters,
    hasData: data !== undefined,
    isLoading,
    mutate,
    sorting,
    titleSearch: titleSearch ?? "",
    handleSortingChange,
    handleTitleSearchChange,
  };
};

export type DocumentVersionsListModel = ReturnType<
  typeof useDocumentVersionsListModel
>;
